import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Avatar,
  Badge,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Button,
  Tooltip,
} from '@mui/material';
import { Bell, Menu as MenuIcon, ChevronDown, User, LogOut } from 'lucide-react';
import { getData, patchData } from '../Webservice/Webservice';

const POLL_INTERVAL_MS = 30000;

const readUser = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const first = user.firstName || user.name || user.fullName || 'User';
    const last = user.lastName || user.lName || '';
    return {
      name: last ? `${first} ${last}` : first,
      role: (user.role || '').replace(/_/g, ' '),
      avatar: user.avatar || user.profileImage || user.image || null,
    };
  } catch {
    return { name: 'User', role: '', avatar: null };
  }
};

const timeAgo = (dateString) => {
  const diff = Math.floor((Date.now() - new Date(dateString)) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const Navbar = ({ onMenuToggle, onLogout }) => {
  const navigate = useNavigate();
  const isVendor = (sessionStorage.getItem('userType') || '').toLowerCase() === 'vendor';
  const basePath = isVendor ? '/seller' : '';

  const [user, setUser] = useState(readUser);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [recent, setRecent] = useState([]);

  const authToken = () => {
    const raw = sessionStorage.getItem('commonToken') || sessionStorage.getItem('token');
    return raw ? `Bearer ${raw}` : '';
  };

  useEffect(() => {
    const refreshUser = () => setUser(readUser());
    window.addEventListener('userUpdate', refreshUser);
    return () => window.removeEventListener('userUpdate', refreshUser);
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    const token = authToken();
    if (!token) return;
    try {
      const userModel = isVendor ? 'Seller' : 'User';
      const res = await getData(`notification/unread-count?userModel=${userModel}`, null, token, false, '', true);
      if (res?.success) setUnreadCount(res.unreadCount || 0);
    } catch {
      // Polling failure is non-fatal; the next tick retries.
    }
  }, [isVendor]);

  useEffect(() => {
    fetchUnreadCount();
    const id = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    // The Notifications page calls this after mark-as-read so the badge updates immediately.
    window.refreshNotificationCount = fetchUnreadCount;
    return () => {
      clearInterval(id);
      delete window.refreshNotificationCount;
    };
  }, [fetchUnreadCount]);

  const openNotifications = async (event) => {
    setNotifAnchor(event.currentTarget);
    try {
      const endpoint = isVendor ? 'vendor/notification/vend' : 'notification/admin';
      const res = await getData(`${endpoint}?page=1&limit=5`, null, authToken(), false, '', true);
      if (res?.success) setRecent(res.data || []);
    } catch {
      setRecent([]);
    }
  };

  const handleNotificationClick = async (n) => {
    if (!n.isRead) {
      try {
        await patchData(`notification/read/${n._id}`, {}, authToken());
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {
        // Leave it unread; the notifications page can retry.
      }
    }
    setNotifAnchor(null);
    navigate(`${basePath}/notification`);
  };

  return (
    <Box
      component="header"
      sx={{
        height: 64,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 1.5, md: 3 },
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}
    >
      <IconButton onClick={onMenuToggle} sx={{ display: { md: 'none' }, color: 'black' }} aria-label="Open menu">
        <MenuIcon size={24} />
      </IconButton>
      <Box sx={{ flex: 1 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
        <Tooltip title="Notifications">
          <IconButton onClick={openNotifications} aria-label="Notifications">
            <Badge badgeContent={unreadCount} color="error" max={99}>
              <Bell size={22} color="#374151" />
            </Badge>
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />

        <Box
          onClick={(e) => setProfileAnchor(e.currentTarget)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer', borderRadius: 2, px: 1, py: 0.5, '&:hover': { background: '#f3f4f6' } }}
        >
          <Avatar src={user.avatar || undefined} alt={user.name} sx={{ width: 36, height: 36, bgcolor: 'rgb(255 209 2)', color: 'black', fontWeight: 700 }}>
            {user.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ display: { xs: 'none', sm: 'block' }, lineHeight: 1.2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, fontFamily: 'Mulish, sans-serif' }}>{user.name}</Typography>
            {user.role && (
              <Typography sx={{ fontSize: 12, color: '#6b7280', textTransform: 'capitalize', fontFamily: 'Mulish, sans-serif' }}>
                {user.role}
              </Typography>
            )}
          </Box>
          <ChevronDown size={16} color="#6b7280" />
        </Box>
      </Box>

      <Menu
        anchorEl={notifAnchor}
        open={Boolean(notifAnchor)}
        onClose={() => setNotifAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 340, maxWidth: '90vw' } }}
      >
        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 700 }}>Notifications</Typography>
          {unreadCount > 0 && <Typography sx={{ fontSize: 12, color: '#6b7280' }}>{unreadCount} unread</Typography>}
        </Box>
        <Divider />
        {recent.length === 0 ? (
          <Typography sx={{ px: 2, py: 3, color: '#6b7280', textAlign: 'center', fontSize: 14 }}>No notifications</Typography>
        ) : (
          recent.map((n) => (
            <MenuItem
              key={n._id}
              onClick={() => handleNotificationClick(n)}
              sx={{ whiteSpace: 'normal', alignItems: 'flex-start', background: n.isRead ? 'transparent' : '#fffbeb', py: 1.25 }}
            >
              <Box>
                <Typography sx={{ fontWeight: n.isRead ? 500 : 700, fontSize: 14 }}>{n.title}</Typography>
                <Typography sx={{ fontSize: 13, color: '#4b5563' }}>{n.message}</Typography>
                <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5 }}>{timeAgo(n.createdAt)}</Typography>
              </Box>
            </MenuItem>
          ))
        )}
        <Divider />
        <Box sx={{ textAlign: 'center', py: 0.5 }}>
          <Button size="small" onClick={() => { setNotifAnchor(null); navigate(`${basePath}/notification`); }}>
            View all
          </Button>
        </Box>
      </Menu>

      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={() => setProfileAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => { setProfileAnchor(null); navigate(`${basePath}/profile`); }}>
          <ListItemIcon><User size={18} /></ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={() => { setProfileAnchor(null); onLogout?.(); }} sx={{ color: '#dc2626' }}>
          <ListItemIcon><LogOut size={18} color="#dc2626" /></ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Navbar;
